import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FiSearch, FiUser, FiCalendar, FiMapPin, FiUserPlus } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import EditPlayerModal from '../components/EditPlayerModal';
import '../styles/playersManagement.css';

export default function PlayersManagement() {
    const dispatch = useDispatch();
    const history = useHistory();
    const userState = useSelector(state => state.user);
    const user = (userState && userState.userData && userState.userData.data) || {};
    const [showCreateModal, setShowCreateModal] = useState(false);
    
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const RESULTS_PER_PAGE = 50;

    useEffect(() => {
        if (searchQuery.trim().length >= 2) {
            const timer = setTimeout(() => {
                setCurrentPage(1);
                handleSearch(1);
            }, 500);
            
            return () => clearTimeout(timer);
        } else {
            setSearchResults([]);
            setHasSearched(false);
            setCurrentPage(1);
            setTotalResults(0);
        }
    }, [searchQuery]);

    const handleSearch = async (page = 1) => {
        const query = searchQuery.trim();
        
        if (query.length < 2) {
            alert('Введите минимум 2 символа для поиска');
            return;
        }

        setIsSearching(true);
        setHasSearched(true);

        try {
            const isTokenExpired = checkIfTokenExpired();
            if (isTokenExpired) {
                await dispatch(refreshTokens());
            }

            const tokens = getTokensFromStore();

            const parts = query.split(' ').filter(p => p.length > 0);
            const searchParams = {};

            if (parts.length >= 2) {
                searchParams.family = parts[0];
                searchParams.name = parts[1];
            } else {
                searchParams.family = parts[0];
            }

            const response = await axios.post(
                BASE_URL.url + API_URLS.searchPlayersByParams,
                searchParams,
                {
                    headers: {
                        "Authorization": "Bearer " + tokens.access_token,
                        'Content-Type': 'application/json',
                    }
                }
            );

            const players = Array.isArray(response.data) ? response.data : 
                           response.data?.data ? response.data.data : [];

            setTotalResults(players.length);
            
            // Пагинация на фронтенде
            const startIndex = (page - 1) * RESULTS_PER_PAGE;
            const endIndex = startIndex + RESULTS_PER_PAGE;
            const paginatedPlayers = players.slice(startIndex, endIndex);
            
            setSearchResults(paginatedPlayers);
            setCurrentPage(page);
        } catch (error) {
            console.error('Search error:', error);
            alert('Ошибка при поиске игроков');
            setSearchResults([]);
            setTotalResults(0);
        } finally {
            setIsSearching(false);
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        handleSearch(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const totalPages = Math.ceil(totalResults / RESULTS_PER_PAGE);

    const handlePlayerClick = (playerId) => {
        history.push(`/player/${playerId}`);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            setCurrentPage(1);
            handleSearch(1);
        }
    };

    // После создания игрока сразу открываем его карточку: оттуда
    // назначают капитаном и добавляют в заявки
    const handlePlayerCreated = (newPlayer) => {
        setShowCreateModal(false);
        const newId = newPlayer && (newPlayer.id || newPlayer.player_id);
        if (newId) {
            history.push(`/player/${newId}`);
        }
    };

    return (
        <div className="layoutWrapper">
            <div className="matchCenterWrapper">
                <div className="matchCenterHeader">
                    <span className="playersManagementName">Управление игроками</span>
                    <p className="pageDescription">
                        Поиск игроков по базе данных. Введите фамилию или фамилию и имя для поиска.
                    </p>
                </div>

                <div className="playersCreateRow">
                    <button
                        type="button"
                        className="createPlayerButton"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <FiUserPlus /> Создать игрока
                    </button>
                    <span className="createPlayerHint">
                        Если игрока нет в базе — заведите его здесь, а потом откройте карточку,
                        чтобы назначить капитаном или добавить в заявку.
                    </span>
                </div>

                <div className="searchSection">
                    <div className="searchInputWrapper">
                        <input
                            type="text"
                            placeholder="Введите фамилию и/или имя игрока (минимум 2 символа)"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isSearching}
                            className="searchInput"
                        />
                        <button 
                            className="searchButton"
                            onClick={() => handleSearch(1)}
                            disabled={isSearching || searchQuery.trim().length < 2}
                        >
                            {isSearching ? 'Поиск...' : 'Искать'}
                        </button>
                    </div>
                    <small className="searchHint">
                        Введите фамилию или фамилию и имя через пробел (например: Иванов Иван). Автоматический поиск при 2+ символах.
                    </small>
                </div>

                <div className="searchResultsSection">
                    {isSearching ? (
                        <div className="loadingState">
                            <div className="spinner"></div>
                            <p>Поиск игроков...</p>
                        </div>
                    ) : hasSearched ? (
                        searchResults.length > 0 ? (
                            <div className="playersListContainer">
                                <div className="resultsHeader">
                                    <h3>Найдено игроков: {totalResults}</h3>
                                    <span className="pageInfo">
                                        Страница {currentPage} из {totalPages} 
                                        (показаны {((currentPage - 1) * RESULTS_PER_PAGE) + 1}-{Math.min(currentPage * RESULTS_PER_PAGE, totalResults)})
                                    </span>
                                </div>
                                <div className="playersTable">
                                    <div className="playersTableHeader">
                                        <div className="tableCell photoCell">Фото</div>
                                        <div className="tableCell nameCell">ФИО</div>
                                        <div className="tableCell dateCell">Дата рождения</div>
                                        <div className="tableCell positionCell">Позиция</div>
                                        <div className="tableCell idCell">ID</div>
                                        <div className="tableCell actionCell"></div>
                                    </div>
                                    {searchResults.map((player) => {
                                        const playerId = player.id || player.player_id || player.playerId;
                                        const playerPhoto = player.image || player.photo || player.avatar;
                                        const birthday = player.birthday || player.birth_date || player.birthDate;
                                        const position = player.position || player.position_name;

                                        return (
                                            <div 
                                                key={playerId} 
                                                className="playerRow"
                                                onClick={() => handlePlayerClick(playerId)}
                                            >
                                                <div className="tableCell photoCell">
                                                    <img 
                                                        src={
                                                            playerPhoto 
                                                                ? (playerPhoto.startsWith('/') 
                                                                    ? BASE_URL.url + playerPhoto 
                                                                    : playerPhoto.startsWith('data:') 
                                                                        ? playerPhoto 
                                                                        : `data:image/jpeg;base64,${playerPhoto}`)
                                                                : defaultTeamLogo
                                                        }
                                                        alt={player.family || 'Игрок'}
                                                        className="playerRowPhoto"
                                                        onError={(e) => { e.target.src = defaultTeamLogo; }}
                                                    />
                                                </div>
                                                <div className="tableCell nameCell">
                                                    {player.family} {player.name} {player.father_name}
                                                </div>
                                                <div className="tableCell dateCell">
                                                    {birthday && (
                                                        <span className="dateValue">
                                                            <FiCalendar />
                                                            {new Date(birthday).toLocaleDateString('ru-RU')}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="tableCell positionCell">
                                                    {position && (
                                                        <span className="positionValue">
                                                            <FiMapPin />
                                                            {position}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="tableCell idCell">
                                                    <span className="playerIdBadge">
                                                        {playerId}
                                                    </span>
                                                </div>
                                                <div className="tableCell actionCell">
                                                    <span className="arrowIcon">→</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {totalPages > 1 && (
                                    <div className="pagination">
                                        <button
                                            className="paginationButton"
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        >
                                            ««
                                        </button>
                                        <button
                                            className="paginationButton"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            «
                                        </button>
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`paginationButton ${currentPage === pageNum ? 'active' : ''}`}
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                        <button
                                            className="paginationButton"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            »
                                        </button>
                                        <button
                                            className="paginationButton"
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        >
                                            »»
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="emptyState">
                                <FiUser size={64} />
                                <h3>Игроки не найдены</h3>
                                <p>Попробуйте изменить условия поиска</p>
                            </div>
                        )
                    ) : (
                        <div className="emptyState">
                            <FiSearch size={64} />
                            <h3>Начните поиск</h3>
                            <p>Введите фамилию или имя игрока в поле поиска</p>
                        </div>
                    )}
                </div>
            </div>

            <EditPlayerModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handlePlayerCreated}
                player={null}
                isCreateMode
                userData={user}
            />
        </div>
    );
}
